/**
 * Claude Vision Extraction
 *
 * Uses Claude Sonnet for equipment label extraction with a focus on
 * conservative, high-precision extraction. This is used as a verification
 * model alongside GPT-4o in the consensus extraction pipeline.
 *
 * Key differences from GPT-4o extraction:
 * - More conservative prompt (only report clearly visible data)
 * - Stronger emphasis on critical fields (manufacturer, model_number)
 * - Lower temperature for consistency
 */

import type { AIRawExtraction } from '@/types'
import {
  buildDisambiguationPrompt,
  buildCapacityGuidance,
  buildDualFuelGuidance,
  buildMultiColumnGuidance,
} from './label-guidance'
import { aiProxy } from '../proxy'

interface ClaudeMessageContent {
  type: 'text'
  text: string
}

interface ClaudeResponse {
  content: ClaudeMessageContent[]
  model: string
  stop_reason: string
}

/**
 * Conservative prompt focused on high-precision extraction using read-first approach.
 * Claude is better at refusing to guess than GPT-4o.
 */
const CLAUDE_EXTRACTION_PROMPT = `You are a precise equipment nameplate reader. Extract ONLY what you can clearly see.

## STEP 1: READ EVERYTHING VISIBLE (MANDATORY)
SLOWLY and CAREFULLY scan the ENTIRE image from top to bottom, left to right.
Read ALL text, numbers, labels, and markings on this equipment label/nameplate:
- Every word, number, and symbol you can see
- All labels and their associated values (especially look for "CAPACITY", "RATED", "MEASURED", "GAL")
- Logos, brand names, certification marks
- Fine print and warnings
- Anything handwritten or stamped

IMPORTANT: Record everything you read in the "raw_text" field before categorizing.

## STEP 2: UNDERSTAND THE LABEL LAYOUT
${buildMultiColumnGuidance()}

${buildDualFuelGuidance()}

## STEP 3: ANALYZE AND CATEGORIZE
Now analyze what each piece of information represents using context clues:
- The LARGEST text is usually the manufacturer/brand
- Text next to "MODEL", "MOD.", "M/N" = model number
- Text next to "SERIAL", "S/N", "SER" = serial number
- Numbers with "V", "VOLTS" = voltage
- Numbers with "A", "AMPS", "FLA" = amperage
- Numbers with "BTU" = heating/cooling capacity
- Numbers with "GPH" = flow rate (NOT tank capacity)

${buildCapacityGuidance()}

${buildDisambiguationPrompt()}

## EQUIPMENT TYPE IDENTIFICATION
Determine equipment type from brand names and labels:
- Water heaters: Lochinvar, Rheem, A.O. Smith, Bradford White, State
- HVAC: Carrier, Trane, Lennox, Daikin, York, Goodman, Bryant
- Kitchen: True, Hobart, Hoshizaki, Manitowoc, Vulcan, Garland
- Look for explicit labels like "WATER HEATER", "AIR CONDITIONER", "ICE MACHINE"

## KEY PRINCIPLES
1. **Scan thoroughly** - Examine every part of the image, don't rush
2. **Explicit labels beat inference** - Always prefer printed values over model number inference
3. **Report exact values** - Use what's printed, not inferred
4. **Use context** - Nearby labels identify what numbers mean
5. **Dual values** - If showing two values (e.g., "65/64"), use the first (rated) value
6. **Never guess** - Use null if unclear or less than 90% confident
7. **RECOVERY ≠ CAPACITY** - "RECOVERY XX GPH" is a flow rate, NOT the tank capacity

## SPECIAL NOTES
- "First Hour Rating" ≠ tank capacity (different measurement)
- "MCA" = Minimum Circuit Ampacity (electrical planning)
- "MOCP" = Maximum Overcurrent Protection (breaker size)
- Series/Product Line often appears as model number prefix (e.g., "SWR" in SWR125N)

Return valid JSON matching this structure:
{
  "manufacturer": "string or null",
  "series": "string or null (product line/series, e.g., SWR, Shield, XR15)",
  "model_number": "string or null",
  "serial_number": "string or null",
  "part_number": "string or null",
  "category": "string (RTU, Split System, Walk-in, etc.)",
  "subcategory": "string or null",
  "confidence": 0.0-1.0,
  "country_of_origin": "string or null",

  "electrical": {
    "voltage": number or null,
    "voltage_range": {"min": number, "max": number} or null,
    "phase": number or null,
    "fla": number or null,
    "rla": number or null,
    "lra": number or null,
    "amps": number or null,
    "mca": number or null,
    "mocp": number or null,
    "hz": number or null,
    "watts": number or null,
    "kw": number or null
  },

  "capacity": {
    "btu": number or null,
    "btu_heating": number or null,
    "tons": number or null,
    "hp": number or null,
    "cfm": number or null,
    "seer": number or null,
    "eer": number or null
  },

  "refrigerant": {
    "type": "string or null",
    "charge_oz": number or null,
    "charge_lbs": number or null
  },

  "water_heater": {
    "type": "string or null (tankless, tank, heat_pump, indirect, commercial_boiler)",
    "tank_capacity_gallons": number or null,
    "energy_factor": number or null,
    "uef_rating": number or null,
    "venting_type": "string or null (direct_vent, power_vent, atmospheric, condensing)",
    "recovery_rate_gph": number or null,
    "first_hour_rating": number or null,
    "gas_line_size": "string or null"
  },

  "fuel": {
    "type": "string or null (natural_gas, propane, oil)",
    "input_btu_hr": number or null,
    "minimum_input_btu_hr": number or null,
    "orifice_size": "string or null",
    "gas_pressure": "string or null"
  },

  "physical": {
    "width_in": number or null,
    "height_in": number or null,
    "depth_in": number or null,
    "weight_lbs": number or null
  },

  "certifications": ["array of visible certifications"],
  "identified_features": ["other notable features visible"],
  "raw_text": "any other visible text not captured above"
}

IMPORTANT: Be conservative. It's better to return null than to guess incorrectly.`

/**
 * Extract equipment data using Claude Sonnet vision
 *
 * @param imageFile - The image file to analyze
 * @returns Partial extraction focused on high-confidence fields
 */
export async function extractWithClaude(imageFile: File): Promise<AIRawExtraction> {
  const base64Image = await fileToBase64(imageFile)
  const mediaType = getMediaType(imageFile.type)

  const data = await aiProxy<ClaudeResponse>('anthropic', '/v1/messages', {
    model: 'claude-sonnet-4-6',
    max_tokens: 8192,
    temperature: 0,
    messages: [
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: CLAUDE_EXTRACTION_PROMPT,
          },
          {
            type: 'image',
            source: {
              type: 'base64',
              media_type: mediaType,
              data: base64Image,
            },
          },
        ],
      },
    ],
  }, {
    'anthropic-version': '2023-06-01',
  })
  const content = data.content[0]?.type === 'text' ? data.content[0].text : ''

  if (!content) {
    throw new Error('No response from Claude')
  }

  try {
    // Strip markdown code blocks if present
    let cleanContent = content
    if (content.includes('```')) {
      cleanContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '')
    }

    const jsonMatch = cleanContent.match(/\{[\s\S]*\}/)
    let jsonStr = jsonMatch?.[0]

    // If no complete JSON object found, the response was likely truncated
    if (!jsonStr) {
      const partialMatch = cleanContent.match(/\{[\s\S]*/)
      if (partialMatch) {
        jsonStr = repairTruncatedJson(partialMatch[0])
        console.warn('⚠️ Claude response was truncated — attempting JSON repair')
      } else {
        throw new Error('No JSON found in response')
      }
    }

    let parsed: Record<string, unknown>
    try {
      parsed = JSON.parse(jsonStr)
    } catch {
      const repaired = repairTruncatedJson(jsonStr)
      console.warn('⚠️ Claude JSON malformed — attempting repair')
      parsed = JSON.parse(repaired)
    }

    console.log('🤖 Claude Extraction:', JSON.stringify(parsed, null, 2))

    // Return with the same structure as GPT extraction
    return normalizeClaudeResponse(parsed)
  } catch (parseError) {
    console.error('Failed to parse Claude response:', content)
    throw new Error('Failed to parse Claude extraction response')
  }
}

/**
 * Normalize Claude response to match AIRawExtraction type
 */
function normalizeClaudeResponse(parsed: Record<string, unknown>): AIRawExtraction {
  const electrical = (parsed.electrical as Record<string, unknown>) || {}
  const capacity = (parsed.capacity as Record<string, unknown>) || {}
  const refrigerant = (parsed.refrigerant as Record<string, unknown>) || {}
  const fuel = (parsed.fuel as Record<string, unknown>) || {}
  const physical = (parsed.physical as Record<string, unknown>) || {}
  const water_heater = (parsed.water_heater as Record<string, unknown>) || {}

  return {
    manufacturer: (parsed.manufacturer as string) ?? null,
    series: (parsed.series as string) ?? null,
    model_number: (parsed.model_number as string) ?? null,
    serial_number: (parsed.serial_number as string) ?? null,
    part_number: (parsed.part_number as string) ?? null,
    asset_tag: (parsed.asset_tag as string) ?? null,
    category: (parsed.category as string) || 'Unknown',
    subcategory: (parsed.subcategory as string) ?? null,
    confidence: (parsed.confidence as number) ?? 0.5,
    country_of_origin: (parsed.country_of_origin as string) ?? null,

    electrical: {
      voltage: (electrical.voltage as number) ?? null,
      voltage_range: (electrical.voltage_range as { min: number; max: number }) ?? null,
      phase: (electrical.phase as number) ?? null,
      fla: (electrical.fla as number) ?? null,
      rla: (electrical.rla as number) ?? null,
      lra: (electrical.lra as number) ?? null,
      hz: (electrical.hz as number) ?? null,
      watts: (electrical.watts as number) ?? null,
      kw: (electrical.kw as number) ?? null,
      amps: (electrical.amps as number) ?? null,
      mca: (electrical.mca as number) ?? null,
      mocp: (electrical.mocp as number) ?? null,
    },

    capacity: {
      btu: (capacity.btu as number) ?? null,
      btu_heating: (capacity.btu_heating as number) ?? null,
      tons: (capacity.tons as number) ?? null,
      hp: (capacity.hp as number) ?? null,
      cfm: (capacity.cfm as number) ?? null,
      seer: (capacity.seer as number) ?? null,
      eer: (capacity.eer as number) ?? null,
      gpm: (capacity.gpm as number) ?? null,
      psi: (capacity.psi as number) ?? null,
    },

    refrigerant: {
      type: (refrigerant.type as string) ?? null,
      charge_oz: (refrigerant.charge_oz as number) ?? null,
      charge_lbs: (refrigerant.charge_lbs as number) ?? null,
    },

    fuel: Object.keys(fuel).length > 0
      ? {
          type: (fuel.type as string) ?? null,
          input_btu_hr: (fuel.input_btu_hr as number) ?? null,
          minimum_input_btu_hr: (fuel.minimum_input_btu_hr as number) ?? null,
          orifice_size: (fuel.orifice_size as string) ?? null,
          gas_pressure: (fuel.gas_pressure as string) ?? null,
        }
      : undefined,

    water_heater: {
      type: (water_heater.type as string) ?? null,
      tank_capacity_gallons: (water_heater.tank_capacity_gallons as number) ?? null,
      energy_factor: (water_heater.energy_factor as number) ?? null,
      uef_rating: (water_heater.uef_rating as number) ?? null,
      venting_type: (water_heater.venting_type as string) ?? null,
      first_hour_rating: (water_heater.first_hour_rating as number) ?? null,
      gas_line_size: (water_heater.gas_line_size as string) ?? null,
    },

    // Water/plumbing - also include recovery rate from water_heater
    water: {
      recovery_rate_gph: (water_heater.recovery_rate_gph as number) ?? null,
      temp_rise_f: null,
      gpm_rating: null,
    },

    physical: {
      width_in: (physical.width_in as number) ?? null,
      height_in: (physical.height_in as number) ?? null,
      depth_in: (physical.depth_in as number) ?? null,
      weight_lbs: (physical.weight_lbs as number) ?? null,
    },

    dates: {
      manufacture_date: null,
      date_code: null,
      service_due: null,
    },

    certifications: (parsed.certifications as string[]) || [],
    identified_features: (parsed.identified_features as string[]) || [],
    condition_notes: [],
    raw_text: (parsed.raw_text as string) ?? null,
  }
}

/**
 * Attempt to repair truncated JSON from a cut-off API response.
 * Closes any open strings, arrays, and objects so JSON.parse can succeed.
 */
function repairTruncatedJson(json: string): string {
  let repaired = json

  // If we're inside an unclosed string value, close it
  const unescapedQuotes = repaired.match(/(?<!\\)"/g)
  if (unescapedQuotes && unescapedQuotes.length % 2 !== 0) {
    const lastCompleteComma = repaired.lastIndexOf(',')
    const lastCompleteBrace = repaired.lastIndexOf('}')
    const lastCompleteBracket = repaired.lastIndexOf(']')
    const cutPoint = Math.max(lastCompleteComma, lastCompleteBrace, lastCompleteBracket)
    if (cutPoint > 0) {
      repaired = repaired.substring(0, cutPoint + 1)
    } else {
      repaired += '"'
    }
  }

  // Remove any trailing comma before we close brackets
  repaired = repaired.replace(/,\s*$/, '')

  // Count open vs close brackets and braces
  let openBraces = 0
  let openBrackets = 0
  let inString = false

  for (let i = 0; i < repaired.length; i++) {
    const ch = repaired[i]
    if (ch === '\\' && inString) { i++; continue }
    if (ch === '"') { inString = !inString; continue }
    if (inString) continue
    if (ch === '{') openBraces++
    if (ch === '}') openBraces--
    if (ch === '[') openBrackets++
    if (ch === ']') openBrackets--
  }

  while (openBrackets > 0) { repaired += ']'; openBrackets-- }
  while (openBraces > 0) { repaired += '}'; openBraces-- }

  return repaired
}

/**
 * Convert File to base64 string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      // Remove the data URL prefix
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = (error) => reject(error)
  })
}

/**
 * Get the media type for Claude API
 */
function getMediaType(mimeType: string): 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp' {
  const typeMap: Record<string, 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp'> = {
    'image/jpeg': 'image/jpeg',
    'image/jpg': 'image/jpeg',
    'image/png': 'image/png',
    'image/gif': 'image/gif',
    'image/webp': 'image/webp',
  }
  return typeMap[mimeType] || 'image/jpeg'
}
